import { Category, Filters } from '@firmable/contracts';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * This is the master store for handling all the search list related client state.
 * A store to maintain UI states (open a sheet, close a sheet, clear the view), not the actual search results.
 * Please DON'T use this store to store the search results.
 */

/**
 * filters - Adding new filter group screen
 * filter-option - Adding options to existing filter that are already applied to the search results.
 */

// create discriminated union type for all the possible state of save mode
export type Mode = 'idle' | 'init' | 'update' | 'processing' | 'success' | 'error';

type AllFilters = 'all-filters';

type Action = 'save' | 'delete' | 'rename';

export interface FilterState {
  isAddFiltersActive: boolean;
  isFilterOptionsActive: boolean;
  identifier: Filters.Identifiers | AllFilters;
  title: string;
  isExpanded: boolean;
  mode: Mode;
  action: Action;
  category: Category;
  /**
   * Purely a temp solution to differentiate between the filters (name not 100% confirmed yet) vs vertical filters
   */
  source: Filters.Source;
  /**
   * This is used to keep track of the currently open filter.
   * This is used to close the dropdown when a different filter is clicked. (Homepage)
   */
  filterCurrentlyOpen: Filters.Identifiers | null;
  /**
   * This is used to keep track of the core search ai prompt text.
   */
  coreAISearchPromptText: string | null;
  shouldClearAIPrompt: boolean;
  /**
   * A phrase pushed into the AI composer as an editable draft (not applied) —
   * e.g. Teammate's filter reading, so both AI surfaces show the same phrase.
   * The composer consumes it and resets it to null.
   */
  draftAIPromptText: string | null;
  isICPSearch: boolean;
  isAISearching: boolean;
}

type FilterActions = {
  setExpanded: () => void;
  setMode: (mode: Mode, action: Action) => void;
  setFiltersSource: (source: Filters.Source) => void;
  changeCategory: (mode: Category) => void;
  openAddFilters: () => void;
  closeAddFilters: () => void;
  openFiltersOptions: ({ identifier, title }: { identifier: Filters.Identifiers; title: string }) => void;
  closeFiltersOptions: () => void;
  setFilterCurrentlyOpen: (identifier: Filters.Identifiers | null) => void;
  setCoreAISearchPromptText: (text: string | null) => void;
  setDraftAIPromptText: (text: string | null) => void;
  // will clear coreAISearchPromptText: null and shouldClearAIPrompt: true
  setClearAll: () => void;
  clearAISearchPrompt: () => void;
  triggerAIPromptClear: () => void;
  setIsICPSearch: (isICPSearch: boolean) => void;
  setIsAISearching: (isAISearching: boolean) => void;
};

/**
 * Store to maintain the state of the end to end filter flow.
 */

const useFilter = create<FilterState & FilterActions>()(
  devtools(
    immer((set) => ({
      category: 'company',
      filterCurrentlyOpen: null,
      source: 'core-filters',
      isAddFiltersActive: false,
      isFilterOptionsActive: false,
      identifier: 'all-filters',
      title: 'Add Filters',
      isExpanded: true,
      mode: 'idle',
      action: 'save',
      coreAISearchPromptText: null,
      shouldClearAIPrompt: false,
      draftAIPromptText: null,
      isICPSearch: false,
      isAISearching: false,
      openAddFilters: () =>
        set(
          {
            isAddFiltersActive: true,
            title: 'Add Filters',
            identifier: 'all-filters',
          },
          false,
          {
            type: 'OPEN_ADD_FILTERS',
          },
        ),
      closeAddFilters: () =>
        set({ isAddFiltersActive: false, identifier: undefined }, false, { type: 'CLOSE_ADD_FILTERS' }),
      openFiltersOptions: ({ identifier, title }) => {
        set(
          (state) => {
            state.isFilterOptionsActive = true;
            state.identifier = identifier;
            state.title = title;
          },
          false,
          {
            type: 'OPEN_FILTERS_OPTIONS',
          },
        );
      },
      closeFiltersOptions: () =>
        set({ isFilterOptionsActive: false, identifier: undefined }, false, {
          type: 'CLOSE_FILTERS_OPTIONS',
        }),
      changeCategory: (category: Category) => set({ category }, false, { type: 'CHANGE_CATEGORY' }),
      setExpanded: () =>
        set(
          ({ isExpanded }) => {
            return { isExpanded: !isExpanded };
          },
          false,
          {
            type: 'SET_EXPANDED',
          },
        ),
      setMode: (mode: Mode, action: Action) =>
        set(
          (state) => {
            state.mode = mode;
            state.action = action;
          },
          false,
          { type: 'SET_SAVE_MODE' },
        ),
      setFiltersSource: (source: Filters.Source) => set({ source }, false, { type: 'SET_FETCH_FILTERS_FROM' }),
      setFilterCurrentlyOpen: (identifier: Filters.Identifiers | null) =>
        set({ filterCurrentlyOpen: identifier }, false, { type: 'SET_FILTER_CURRENTLY_OPEN' }),
      setCoreAISearchPromptText: (text: string | null) =>
        set({ coreAISearchPromptText: text }, false, { type: 'SET_CORE_AI_SEARCH_PROMPT_TEXT' }),
      setDraftAIPromptText: (text) =>
        set({ draftAIPromptText: text }, false, { type: 'SET_DRAFT_AI_PROMPT_TEXT' }),
      setClearAll: () =>
        set({ coreAISearchPromptText: null, shouldClearAIPrompt: true }, false, { type: 'SET_CLEAR_ALL' }),
      clearAISearchPrompt: () => set({ shouldClearAIPrompt: false }),
      triggerAIPromptClear: () => set({ shouldClearAIPrompt: true }),
      setIsICPSearch: (isICPSearch: boolean) => set({ isICPSearch }, false, { type: 'SET_ICP_SEARCH' }),
      setIsAISearching: (isAISearching: boolean) => set({ isAISearching }, false, { type: 'SET_AI_SEARCHING' }),
    })),
    {
      store: 'ManageFilter',
    },
  ),
);

export { useFilter };
